import { Box, Button, Checkbox, CircularProgress, FormControlLabel, Stack, TextField, Typography } from "@mui/material";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { DialogPlanLlmPrompt, DialogPlanLlmPromptApi, DialogPlanLlmPromptQuizQuestionAudio } from "../../api/DialogPlanLlmPromptApi";
import AppBarContext from '../../context/AppBarContext';
import { handleErrorMessage } from '../ErrorDialog';

interface DialogPlanLlmPromptQuizQuestionAudioFormProps {
    dialogPlanLlmPromptId?: number;
    dialogPlanLlmPrompt?: DialogPlanLlmPromptQuizQuestionAudio;
    setDialogPlanLlmPrompt?: (dialogPlanLlmPrompt: DialogPlanLlmPrompt) => void;
    handleLlmPromptChange?: (dialogPlanLlmPrompt: DialogPlanLlmPrompt) => void;
    handleRegenerateDialogPlanLlmPrompt?: (dialogPlanLlmPrompt: DialogPlanLlmPrompt, fieldName: string) => void;
    isRegenerating?: boolean;

    id: number;
    dialogPlanId: number;
    orderIndex: number;
    isTeacher: boolean;
    prompt: string;
    isNoteToStudent: boolean;
    yesResponse: string;
    noResponse: string;
    imageSource: string;
}

export const DialogPlanLlmPromptQuizQuestionAudioForm: React.FC<DialogPlanLlmPromptQuizQuestionAudioFormProps> = ({
    dialogPlanLlmPromptId, dialogPlanLlmPrompt, setDialogPlanLlmPrompt, handleLlmPromptChange, handleRegenerateDialogPlanLlmPrompt, isRegenerating,
    id, dialogPlanId, orderIndex, isTeacher, prompt, isNoteToStudent, yesResponse, noResponse, imageSource }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const [teacherChinesePrompt, setTeacherChinesePrompt] = React.useState<string>('');
    const [displayWordAlso, setDisplayWordAlso] = React.useState<boolean>(false);
    const [correctVocabularyWord, setCorrectVocabularyWord] = React.useState<string | null>('');
    const [correctVocabularyWordImage, setCorrectVocabularyWordImage] = React.useState<string | null>('');
    const [correctVocabularyWordInfoText, setCorrectVocabularyWordInfoText] = React.useState<string | null>('');

    const [incorrectVocabularyWordList, setIncorrectVocabularyWordList] = React.useState<string | null>('');
    const [incorrectVocabularyWordImageList, setIncorrectVocabularyWordImageList] = React.useState<string | null>('');
    const [incorrectVocabularyWordInfoTextList, setIncorrectVocabularyWordInfoTextList] = React.useState<string | null>('');

    const handleTeacherChinesePromptChange = (e: React.ChangeEvent<HTMLInputElement>) => { setTeacherChinesePrompt(e.target.value); };
    const handleDisplayWordAlsoChange = (e: React.ChangeEvent<HTMLInputElement>) => { setDisplayWordAlso(e.target.checked); };
    const handleCorrectVocabularyWordChange = (e: React.ChangeEvent<HTMLInputElement>) => { setCorrectVocabularyWord(e.target.value); };
    const handleCorrectVocabularyWordImageChange = (e: React.ChangeEvent<HTMLInputElement>) => { setCorrectVocabularyWordImage(e.target.value); };
    const handleCorrectVocabularyWordInfoTextChange = (e: React.ChangeEvent<HTMLInputElement>) => { setCorrectVocabularyWordInfoText(e.target.value); };

    const handleIncorrectVocabularyWordList = (e: React.ChangeEvent<HTMLInputElement>) => { setIncorrectVocabularyWordList(e.target.value); };
    const handleIncorrectVocabularyWordImageList = (e: React.ChangeEvent<HTMLInputElement>) => { setIncorrectVocabularyWordImageList(e.target.value); };
    const handleIncorrectVocabularyWordInfoTextList = (e: React.ChangeEvent<HTMLInputElement>) => { setIncorrectVocabularyWordInfoTextList(e.target.value); };

    React.useEffect(() => {
        if (!dialogPlanLlmPromptId && dialogPlanLlmPrompt) {
            setDialogPlanLlmPromptFields(dialogPlanLlmPrompt);
        }
    }, [dialogPlanLlmPrompt, dialogPlanLlmPromptId])
    React.useEffect(() => {
        if (dialogPlanLlmPromptId && !dialogPlanLlmPrompt) {
            DialogPlanLlmPromptApi.getById(dialogPlanLlmPromptId)
                .then((response) => {
                    const dialogPlanLlmPrompt = response.data as DialogPlanLlmPromptQuizQuestionAudio;
                    setDialogPlanLlmPromptFields(dialogPlanLlmPrompt);
                })
                .catch(err => {
                    handleErrorMessage(err, setErrorMessage, history, location);
                })
        }
    }, [dialogPlanLlmPromptId, dialogPlanLlmPrompt, history, location, setErrorMessage])


    function setDialogPlanLlmPromptFields(dialogPlanLlmPrompt: DialogPlanLlmPromptQuizQuestionAudio) {
        if (dialogPlanLlmPrompt.class_name == "DialogPlanLlmPromptQuizQuestionAudioModel") {
            setTeacherChinesePrompt(dialogPlanLlmPrompt.teacher_chinese_prompt);
            setDisplayWordAlso(dialogPlanLlmPrompt.display_word_also);
            setCorrectVocabularyWord(dialogPlanLlmPrompt.correct_vocabulary_word);
            setCorrectVocabularyWordImage(dialogPlanLlmPrompt.correct_vocabulary_word_image);
            setCorrectVocabularyWordInfoText(dialogPlanLlmPrompt.correct_vocabulary_word_info_text);
            setIncorrectVocabularyWordList(dialogPlanLlmPrompt.incorrect_vocabulary_word_list);
            setIncorrectVocabularyWordImageList(dialogPlanLlmPrompt.incorrect_vocabulary_word_image_list);
            setIncorrectVocabularyWordInfoTextList(dialogPlanLlmPrompt.incorrect_vocabulary_word_info_text_list);
        }
    }

    React.useEffect(() => {
        const newDialogPlanLlmPrompt = new DialogPlanLlmPromptQuizQuestionAudio(
            id,
            dialogPlanId,
            orderIndex,
            isTeacher,
            prompt,
            isNoteToStudent,
            yesResponse,
            noResponse,
            imageSource,
            teacherChinesePrompt || dialogPlanLlmPrompt?.teacher_chinese_prompt || '',
            displayWordAlso,
            correctVocabularyWord || dialogPlanLlmPrompt?.correct_vocabulary_word || null,
            correctVocabularyWordImage || dialogPlanLlmPrompt?.correct_vocabulary_word_image || null,
            correctVocabularyWordInfoText || dialogPlanLlmPrompt?.correct_vocabulary_word_info_text || null,
            incorrectVocabularyWordList || dialogPlanLlmPrompt?.incorrect_vocabulary_word_list || null,
            incorrectVocabularyWordImageList || dialogPlanLlmPrompt?.incorrect_vocabulary_word_image_list || null,
            incorrectVocabularyWordInfoTextList || dialogPlanLlmPrompt?.incorrect_vocabulary_word_info_text_list || null,
        );
        if (handleLlmPromptChange) {
            handleLlmPromptChange(newDialogPlanLlmPrompt);
        }
        if (setDialogPlanLlmPrompt) {
            setDialogPlanLlmPrompt(newDialogPlanLlmPrompt);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, dialogPlanId, orderIndex, isTeacher, prompt, isNoteToStudent, yesResponse, noResponse, imageSource, teacherChinesePrompt,
        displayWordAlso, correctVocabularyWord, correctVocabularyWordImage, correctVocabularyWordInfoText,
        incorrectVocabularyWordList, incorrectVocabularyWordImageList, incorrectVocabularyWordInfoTextList])

    return (<>
        <Stack spacing={4} sx={{
            width: dialogPlanLlmPrompt ? '100%' : '90%', // Conditionally set width
            margin: '0 auto', pt: 2, pb: 4
        }}>
            <div style={{ width: '100%' }}>
                <TextField
                    label="Chinese Prompt (optional)"
                    value={teacherChinesePrompt}
                    onChange={handleTeacherChinesePromptChange}
                    fullWidth
                    multiline
                    rows={5}
                />
                <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                    Use &lt;Answer/&gt; to mark where the response is inserted
                </Typography>
                {(handleRegenerateDialogPlanLlmPrompt && dialogPlanLlmPrompt) && <>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button variant="contained"
                            onClick={() => handleRegenerateDialogPlanLlmPrompt(dialogPlanLlmPrompt, "teacher_chinese_prompt")}
                            disabled={isRegenerating}>
                            Regenerate
                            {isRegenerating && <CircularProgress size={16} sx={{ ml: 2 }} />}
                        </Button>
                    </Box>
                </>}
            </div>
            <h3>Correct Answer</h3>
            <TextField
                label="Prompt for Correct Answer Word"
                value={correctVocabularyWord}
                onChange={handleCorrectVocabularyWordChange}
                multiline
                rows={5}
            />
            {(handleRegenerateDialogPlanLlmPrompt && dialogPlanLlmPrompt) && <>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button variant="contained"
                        onClick={() => handleRegenerateDialogPlanLlmPrompt(dialogPlanLlmPrompt, "correct_vocabulary_word")}
                        disabled={isRegenerating}>
                        Regenerate
                        {isRegenerating && <CircularProgress size={16} sx={{ ml: 2 }} />}
                    </Button>
                </Box>
            </>}
            <Box sx={{ pb: 1.25 }}>   {/* ALIGN with generated form*/}
                <FormControlLabel
                    control={<Checkbox checked={displayWordAlso} onChange={handleDisplayWordAlsoChange} />}
                    label="In addition to audio, display word text also"
                />
            </Box>
            <TextField
                label="Prompt for Correct Word Info Button (shown in chat bubble after submit, not next to radio button before)"
                value={correctVocabularyWordInfoText}
                onChange={handleCorrectVocabularyWordInfoTextChange}
                multiline
                rows={5}
            />
            {(handleRegenerateDialogPlanLlmPrompt && dialogPlanLlmPrompt) && <>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button variant="contained"
                        onClick={() => handleRegenerateDialogPlanLlmPrompt(dialogPlanLlmPrompt, "correct_vocabulary_word_info_text")}
                        disabled={isRegenerating}>
                        Regenerate
                        {isRegenerating && <CircularProgress size={16} sx={{ ml: 2 }} />}
                    </Button>
                </Box>
            </>}
            <TextField
                label="Prompt for Correct Answer Image"
                value={correctVocabularyWordImage}
                onChange={handleCorrectVocabularyWordImageChange}
                multiline
                rows={5}
            />
            {(handleRegenerateDialogPlanLlmPrompt && dialogPlanLlmPrompt) && <>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button variant="contained"
                        onClick={() => handleRegenerateDialogPlanLlmPrompt(dialogPlanLlmPrompt, "correct_vocabulary_word_image")}
                        disabled={isRegenerating}>
                        Regenerate
                        {isRegenerating && <CircularProgress size={16} sx={{ ml: 2 }} />}
                    </Button>
                </Box>
            </>}
            {/* spacer for image on the right */}
            <Box sx={{ height: '183px', marginTop: '0px !important' }} />
            <Stack spacing={4} sx={{ width: '90%', margin: '0 auto', pt: 2, pb: 4 }}>
                <h3>Incorrect Answer</h3>
                <TextField
                    label="Prompt for Incorrect Answer Word List"
                    value={incorrectVocabularyWordList}
                    onChange={handleIncorrectVocabularyWordList}
                    multiline
                    rows={5}
                />
                {(handleRegenerateDialogPlanLlmPrompt && dialogPlanLlmPrompt) && <>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button variant="contained"
                            onClick={() => handleRegenerateDialogPlanLlmPrompt(dialogPlanLlmPrompt, "incorrect_vocabulary_word_list")}
                            disabled={isRegenerating}>
                            Regenerate
                            {isRegenerating && <CircularProgress size={16} sx={{ ml: 2 }} />}
                        </Button>
                    </Box>
                </>}
                <TextField
                    label="Prompt for Incorrect Word info text (shown in chat bubble after submit, not next to radio button before)"
                    value={incorrectVocabularyWordInfoTextList}
                    onChange={handleIncorrectVocabularyWordInfoTextList}
                    multiline
                    rows={5}
                />
                {(handleRegenerateDialogPlanLlmPrompt && dialogPlanLlmPrompt) && <>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button variant="contained"
                            onClick={() => handleRegenerateDialogPlanLlmPrompt(dialogPlanLlmPrompt, "incorrect_vocabulary_word_info_text_list")}
                            disabled={isRegenerating}>
                            Regenerate
                            {isRegenerating && <CircularProgress size={16} sx={{ ml: 2 }} />}
                        </Button>
                    </Box>
                </>}
                <TextField
                    label="Prompt for Incorrect Answer Image List"
                    value={incorrectVocabularyWordImageList}
                    onChange={handleIncorrectVocabularyWordImageList}
                    multiline
                    rows={5}
                />
                <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                    &lt;IncorrectVocabularyWord/&gt; will be replaced with
                    the corresponding incorrect vocabulary word when generating.
                </Typography>
                {(handleRegenerateDialogPlanLlmPrompt && dialogPlanLlmPrompt) && <>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Button variant="contained"
                            onClick={() => handleRegenerateDialogPlanLlmPrompt(dialogPlanLlmPrompt, "incorrect_vocabulary_word_image_list")}
                            disabled={isRegenerating}>
                            Regenerate
                            {isRegenerating && <CircularProgress size={16} sx={{ ml: 2 }} />}
                        </Button>
                    </Box>
                </>}
            </Stack>

        </Stack>
    </>);
};
